'use client';

import Link from 'next/link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';
import Navigation from '../../../components/Navigation';
import {
    DocAnswer,
    DocBeforeAfter,
    DocCode,
    DocEyebrow,
    DocMistakeList,
    DocRelated,
    DocSection,
    DocSubtitle,
    DocText,
    DocTitle,
} from '../../../components/DocArticle';
import { encodeBase64 } from '../../../utils/base64';

const diffExampleUrl = (() => {
    const oldEncoded = encodeBase64(JSON.stringify({ name: 'demo', version: 1 }, null, 2));
    const newEncoded = encodeBase64(JSON.stringify({ name: 'demo', version: 2, tags: ['a'] }, null, 2));
    return `/diff?old=${oldEncoded}&new=${newEncoded}`;
})();

const mistakes = [
    {
        mistake: 'Diffing two documents and shipping the result as a "patch"',
        fix: 'A line-based diff (like git produces) is not a JSON Patch. Only a document structured as an array of RFC 6902 operations - add, remove, replace, move, copy, test - can be applied by a JSON Patch library.',
    },
    {
        mistake: 'Using a human-readable diff format for API updates',
        fix: 'Unified diffs describe text lines, not JSON values. Sending one to an API that expects application/json-patch+json will be rejected. Generate the RFC 6902 operations instead.',
    },
    {
        mistake: 'Assuming every diff tool produces the same operations',
        fix: 'RFC 6902 defines how a patch is applied, not how it is generated. Two generators can produce different but equally valid patches for the same pair of documents.',
    },
];

export default function JsonPatchVsJsonDiffPage() {
    return (
        <>
            <Navigation activePage="docs" />
            <main>
                <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
                    {/* Breadcrumb */}
                    <Box component="nav" sx={{ display: 'flex', mb: 4, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
                        <Link href="/" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Home</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Link href="/docs" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Documentation</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Typography component="span" sx={{ color: 'var(--fg)', fontSize: '0.875rem' }}>JSON Patch vs JSON Diff</Typography>
                    </Box>

                    <Box sx={{ maxWidth: '800px' }}>
                        <DocEyebrow>Documentation</DocEyebrow>
                        <DocTitle>JSON Patch vs JSON Diff</DocTitle>
                        <DocSubtitle>
                            The two most confused terms in the JSON world. A <strong>diff</strong> answers "what changed
                            between these two documents?" A <strong>patch</strong> answers "what operations turn this
                            document into that one?" - in a form a machine can apply.
                        </DocSubtitle>

                        <DocAnswer>
                            A JSON diff is a comparison: it shows the differences between two JSON documents for a human
                            to read. JSON Patch (RFC 6902) is a document format: an ordered array of operations
                            (add, remove, replace, move, copy, test) that a library applies to transform one document
                            into another. Every JSON Patch starts life as a diff - but only a patch can be applied.
                        </DocAnswer>

                        <DocSection>What a JSON diff is</DocSection>
                        <DocText>
                            A diff is the output of comparing two documents. There is no single standard for it: git-style
                            unified diffs show changed lines, tree diffs show changed paths, and every online "compare
                            JSON" tool has its own visual convention. What they share is the audience: diffs are made
                            for people. You read one to review a config change, debug an API response, or audit what a
                            deploy touched.
                        </DocText>
                        <DocText>
                            The <Link href="/diff" style={{ color: 'var(--accent)' }}>JSON Diff tool</Link> on this site
                            highlights every added, removed and edited value with its exact path - and because this is a
                            JSON Patch site, it also shows the same change expressed as RFC 6902 operations.
                        </DocText>

                        <DocSection>What a JSON Patch is</DocSection>
                        <DocText>
                            JSON Patch is an IETF standard (<Link href="https://datatracker.ietf.org/doc/html/rfc6902" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>RFC 6902</Link>):
                            a JSON array of operations, each naming an <code>op</code>, a <code>path</code>, and usually
                            a <code>value</code>. Unlike a diff, a patch is executable: any conforming library in any
                            language can apply it, operations run in order, and evaluation stops when an operation fails. RFC 6902 does not require rollback of earlier operations.
                        </DocText>
                        <DocCode label="The same change, as a JSON Patch">{`[
  { "op": "replace", "path": "/version", "value": 2 },
  { "op": "add", "path": "/tags", "value": ["a"] }
]`}</DocCode>
                        <DocBeforeAfter
                            before={`{ "name": "demo", "version": 1 }`}
                            after={`{ "name": "demo", "version": 2, "tags": ["a"] }`}
                        />

                        <DocSection>How the two relate</DocSection>
                        <DocText>
                            Generating a patch <em>is</em> diffing: the generator compares the two documents and emits
                            the operations that describe the differences. Applying a patch is the reverse trip: it
                            reproduces the second document from the first without ever seeing it. That is why the
                            same tool on this site does both - the visual diff is the human view of the change, the
                            patch is the machine view.
                        </DocText>
                        <Box component="ul" sx={{ color: 'var(--fg-muted)', lineHeight: 1.8, pl: 3, mb: 3 }}>
                            <li><strong>Reading a change</strong> - you want a diff: review, debug, audit.</li>
                            <li><strong>Replaying a change</strong> - you want a patch: API updates, config rollouts, syncing state.</li>
                            <li><strong>Guarding a change</strong> - only a patch can do it: the <Link href="/operations/test" style={{ color: 'var(--accent)' }}>test operation</Link> stops evaluation if the document is not in the expected state.</li>
                        </Box>

                        <DocSection>When to use which</DocSection>
                        <DocText>
                            Use a diff when the output is for a person: code review, "what did this deploy change",
                            comparing two API responses. Use a patch when the output is for a program: HTTP PATCH
                            endpoints that accept <code>application/json-patch+json</code>, database updates that should
                            only touch modified fields, or any flow where the change must be replayed exactly and
                            as an ordered sequence. If you need both - review first, apply later - generate the patch and read the
                            visual diff of the same change.
                        </DocText>

                        <DocSection>Common mistakes</DocSection>
                        <DocMistakeList items={mistakes} />

                        {/* CTA */}
                        <Box sx={{
                            mt: 6, p: 4, borderRadius: 'var(--radius-lg)',
                            background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-hover, var(--accent)) 100%)',
                            textAlign: 'center',
                        }}>
                            <Typography variant="h2" sx={{ fontSize: '1.4rem', fontWeight: 700, color: 'white', mb: 1 }}>
                                See a diff and its patch side by side
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.9)', mb: 3 }}>
                                Load a real example into the diff tool - every changed value highlighted, with the RFC 6902 operations underneath.
                            </Typography>
                            <Button
                                size="large"
                                href={diffExampleUrl}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    backgroundColor: 'white', color: 'var(--accent)', fontWeight: 600,
                                    px: 4, py: 1.5, textTransform: 'none',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Open the JSON Diff tool
                            </Button>
                        </Box>

                        <DocRelated links={[
                            { href: '/diff', title: 'JSON Diff tool', description: 'Compare two JSON documents and see every change' },
                            { href: '/guides/json-patch-vs-json-merge-patch', title: 'JSON Patch vs JSON Merge Patch', description: 'RFC 6902 vs RFC 7396 - the other classic comparison' },
                            { href: '/docs/how-to-generate-json-patch', title: 'How to generate a JSON Patch', description: 'Turn a before and after document into operations' },
                            { href: '/operations/test', title: 'Test operation', description: 'Guard patches against unexpected document state' },
                            { href: '/docs', title: 'Documentation', description: 'Back to the JSON Patch documentation hub' },
                        ]} />
                    </Box>
                </Container>
            </main>
        </>
    );
}
